/* Copyright (C) 2019 SmartboxTv S.A. - All rights reserved
 * Unauthorized copying of this file, via any medium is strictly prohibited
 * Proprietary and confidential
 * Written by Erasmo Marín <emarin@smartboxtv.com>
 */

import { toJS, flow } from 'mobx';
import concat from 'lodash/concat';
import isArray from 'lodash/isArray';
import isObject from 'lodash/isObject';
import fetch from '../fetch';
import get from 'lodash/get';
import set from 'lodash/set';
import filter from 'lodash/filter';
import ErrorsStore from './errorsStore';

const isError = (res) => {
    if (isObject(res) && res.error) {
        return true;
    } else if (res && res.items && (isArray(res.items) || isObject(res.items))) {
        let errors = filter(res.items, item => {
            if (item.code && item.info) return true;
            return false;
        });
        if (errors && errors.length > 0) return true;
    } else if (res && (res.networkError || res.graphQLErrors)) {
        return true;
    } else {
        return false;
    }
};

const generateDetail = ({
    _id,
    params = {},
    store,
    url,
    ignoreErrors = false,
    customError,
    disableClientFilter = false,
}) => {
    if (!store || !url) {
        console.warn('Detail action generator: You must provide a store or an url');
        return null;
    }

    return flow(function* () {
        let current = store.details.get(_id) || {};
        current.loading = true;
        store.details.set(_id, current);

        try {
            const detail = yield fetch.get({ url, params, disableClientFilter });
            if (isError(detail) && !ignoreErrors) {
                ErrorsStore.addError(detail, customError);
            }
            store.details.set(_id, detail);
        } catch (e) {
            ErrorsStore.addError(e, customError);
            console.warn(e);
        }
    });
};

const generateList = ({ hash, params, store, url, ignoreErrors = false, customError, disableClientFilter = false }) => {
    if (!store || !url) {
        console.warn('List action generator: You must provide a store or an url');
        return null;
    }

    return flow(function* () {
        let current = store.collection.get(hash) || {};
        current.loading = true;
        store.collection.set(hash, current);

        try {
            const list = yield fetch.get({
                url,
                params,
                disableClientFilter,
            });
            if (isError(list) && !ignoreErrors) {
                ErrorsStore.addError(list, customError);
            }
            store.collection.set(hash, list);
        } catch (e) {
            ErrorsStore.addError(e, customError);
            console.warn(e);
        }
    });
};

const generateListRefresh = ({
    hash,
    params = {},
    store,
    url,
    ignoreErrors = false,
    customError,
    disableClientFilter = false,
    maxPagesToRefresh = 5,
}) => {
    if (!store || !url) {
        console.warn('List refresh action generator: You must provide a store or an url');
        return null;
    }

    return flow(function* () {
        let current = store.collection.get(hash) || {};
        current.loading = true;

        let currentPage = get(current, 'pagination.page', 1);
        let currentItemsPerPage = get(params, 'itemsPerPage', -1);

        if (currentItemsPerPage < 0) {
            console.warn('itemsPerPage parameter must be defined in generateListRefresh');
            return;
        }

        if (currentPage > maxPagesToRefresh) currentPage = maxPagesToRefresh;
        params.itemsPerPage = currentPage * currentItemsPerPage;
        store.collection.set(hash, current);

        try {
            const list = yield fetch.get({
                url,
                params,
                disableClientFilter,
            });
            if (isError(list) && !ignoreErrors) {
                ErrorsStore.addError(list, customError);
            }
            if (current.items) {
                current.items = list.items;
                set(current, 'pagination.page', currentPage);
                store.collection.set(hash, current);
            } else {
                store.collection.set(hash, list);
            }
        } catch (e) {
            ErrorsStore.addError(e, customError);
            console.warn(e);
        }
    });
};

const generateListMore = ({ hash, params = {}, store, url, ignoreErrors = false, customError }) => {
    if (!store || !url) {
        console.warn('List more action generator: You must provide a store or an url');
        return null;
    }

    return flow(function* () {
        const list = store.collection.get(hash) || {};
        const pagination = list.pagination;
        if (
            !pagination ||
            (pagination.first && pagination.last && (pagination.first === pagination.last)) ||
            (pagination.page && pagination.last && (pagination.page === pagination.last)) ||
            (pagination.page && pagination.pages && (pagination.page === pagination.pages))
        ) return false;

        params.page = pagination.page + 1;

        list.loading = true;
        store.collection.set(hash, list);

        try {
            const res = yield fetch.get({
                url,
                params,
            });

            if (isError(res) && !ignoreErrors) {
                ErrorsStore.addError(res, customError);
            } else {
                list.items = concat(toJS(list.items), res.items);
                list.pagination = res.pagination;
                store.collection.set(hash, list);
            }
        } catch (e) {
            ErrorsStore.addError(e, customError);
            console.warn(e);
        }
    });
};

const generateCreate = ({ items, item = {}, store, url, ignoreErrors = false, customError }) => {
    if (!store || !url) {
        console.warn('Create action generator: You must provide a store or an url');
        return null;
    }

    return flow(function* () {
        try {
            const detail = yield fetch.post({
                url,
                body: { data: { items: isArray(items) ? items : [item] } },
            });

            if (isError(detail) && !ignoreErrors) {
                ErrorsStore.addError(detail, customError);
            }
            return detail;
        } catch (e) {
            ErrorsStore.addError(e, customError);
            console.warn(e);
            return false;
        }
    });
};

const generateUpdate = ({ items, item = {}, store, url, ignoreErrors = false, customError, skipDetail = true }) => {
    if (!store || !url) {
        console.warn('Update action generator: You must provide a store or an url');
        return null;
    }

    return flow(function* () {
        try {
            items = isArray(items) ? items : [item];
            const detail = yield fetch.patch({
                url,
                body: { data: { items } },
            });
            /* "skipDetail" is used to avoid making multiple calls to "detail" when a list is updated */
            if (!skipDetail) {
                for (let i = 0; i < items.length; i++) {
                    store.detail({ _id: items[i]._id });
                }
            }

            if (isError(detail) && !ignoreErrors) {
                ErrorsStore.addError(detail, customError);
            }

            return detail;
        } catch (e) {
            ErrorsStore.addError(e, customError);
            console.warn(e);
            return false;
        }
    });
};

const generateUpdateSingle = ({ data, store, url, customError }) => {
    if (!store || !url) {
        console.warn('Update action generator: You must provide a store or an url');
        return null;
    }

    return flow(function* () {
        try {
            const detail = yield fetch.patch({
                url,
                body: { data },
            });
            return detail;
        } catch (e) {
            ErrorsStore.addError(e, customError);
            console.warn(e);
            return false;
        }
    });
};

const generateReplace = ({ items, item = {}, store, url, customError }) => {
    if (!store || !url) {
        console.warn('Replace action generator: You must provide a store or an url');
        return null;
    }

    return flow(function* () {
        try {
            items = isArray(items) ? items : [item];
            const detail = yield fetch.put({
                url,
                body: { data: { items } },
            });
            if (store.detail) {
                for (let i = 0; i < items.length; i++) {
                    store.detail({ _id: items[i]._id });
                }
            }
            return detail;
        } catch (e) {
            ErrorsStore.addError(e, customError);
            console.warn(e);
            return false;
        }
    });
};

const generateDelete = ({ items, _id = false, type = 'logical', store, url, customError }) => {
    if (!store || !url) {
        console.warn('Delete action generator: You must provide a store or an url');
        return null;
    }

    return flow(function* () {
        try {
            const response = yield fetch.delete({
                url,
                body: {
                    data: {
                        items: isArray(items) ? items : [_id],
                        type,
                    },
                },
            });

            if (response && !isError(response) && store.details.has(_id)) store.details.delete(_id);

            store.collection.forEach((value, key, map) => {
                if (value.items) {
                    value.items = value.items.filter(item => {
                        return toJS(item)._id !== _id;
                    });
                }
                return response;
            });

            return response;
        } catch (e) {
            ErrorsStore.addError(e, customError);
            console.warn(e);
            return false;
        }
    });
};

export {
    generateDetail,
    generateList,
    generateListRefresh,
    generateCreate,
    generateUpdate,
    generateUpdateSingle,
    generateListMore,
    generateDelete,
    generateReplace,
    isError,
};
